import React, { useMemo } from 'react';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import ReactSelect from 'react-select';
import { Label } from 'reactstrap';

import { messages } from '../../../_shared/i18n/messages';

import DatePicker from '../DatePicker';
import TimePicker from '../TimePicker';
import TimezonePicker from '../TimezonePicker';

import './DateTimePicker.scss';

const propTypes = {

	/**
	 * The input name.
	 */
	name: PropTypes.string.isRequired,

	/**
	 * The input datetime value (as a MomentJS object).
	 */
	value: PropTypes.instanceOf(moment),

	/**
	 * The default timezone to use. Optional — falls back to the viewer's own
	 * zone, since an embedded form renders wherever the learner is.
	 */
	defaultTimezone: PropTypes.string,

	/**
	 * True, if the input datetime value is invalid.
	 */
	isInvalid: PropTypes.bool.isRequired,

	/**
	 * True, if the input datetime value is required.
	 */
	isRequired: PropTypes.bool.isRequired,

	/**
	 * Callback to handle changes to the datetime value (as a MomentJS object).
	 */
	onChange: PropTypes.func.isRequired,

};

const defaultProps = {
	isInvalid: false,
	isRequired: false,
};

const DateTimePicker = (props) => {

	const {
		name,
		value,
		defaultTimezone = moment.tz.guess(),
		isInvalid,
		isRequired,
		onChange,
	} = props;

	const intl = useIntl();

	const formValues = useMemo(() => {

		let date;

		let time;

		let timezone = defaultTimezone;

		if (value) {

			const formattedValue = value.format('YYYY-MM-DD h:mma');

			date = formattedValue.split(' ')[0];
			time = formattedValue.split(' ')[1];
			timezone = value._z ? value._z.name : defaultTimezone;

		}

		return {
			date,
			time,
			timezone,
		};

	}, [value, defaultTimezone]);

	const handleDateChange = (temp) => {

		let newValue;

		// Check if a valid date value was selected
		if (temp) {

			// Use the selected timezone
			const newTimezone = formValues.timezone;

			// Use the updated date
			const newDate = temp.format('YYYY-MM-DD');

			// Default to current time
			const newTime = formValues.time || moment().tz(newTimezone).format('h:mma');

			newValue = moment.tz(`${newDate} ${newTime}`, 'YYYY-MM-DD h:mma', newTimezone);

		// If a value is required, default to the current date
		} else if (isRequired) {

			// Use the selected timezone
			const newTimezone = formValues.timezone;

			// Default to current date
			const newDate = moment().tz(newTimezone).format('YYYY-MM-DD');

			// Default to current time
			const newTime = formValues.time || moment().tz(newTimezone).format('h:mma');

			newValue = moment.tz(`${newDate} ${newTime}`, 'YYYY-MM-DD h:mma', newTimezone);

		// Otherwise, reset the value
		} else {

			newValue = null;

		}

		onChange(newValue);

	};

	const handleTimeChange = (temp) => {

		let newValue;

		// Check if a valid time value was selected
		if (temp) {

			const newTimezone = formValues.timezone;

			// Default to current date
			const newDate = formValues.date || moment().tz(newTimezone).format('YYYY-MM-DD');

			const newTime = temp;

			newValue = moment.tz(`${newDate} ${newTime}`, 'YYYY-MM-DD h:mma', newTimezone);

		// Otherwise, reset the value
		} else {

			newValue = null;

		}

		onChange(newValue);

	};

	const handleTimezoneChange = (temp) => {

		const newTimezone = temp;

		// Default to current date
		const newDate = formValues.date || moment().tz(newTimezone).format('YYYY-MM-DD');

		// Default to current time
		const newTime = formValues.time || moment().tz(newTimezone).format('h:mma');

		const newValue = moment.tz(`${newDate} ${newTime}`, 'YYYY-MM-DD h:mma', newTimezone);

		onChange(newValue);

	};

	const dateLabel = `${name}_date`;
	const timeLabel = `${name}_time`;
	const timezoneLabel = `${name}_timezone`;

	return (
		<div className={`tc-datetime-picker ${isInvalid ? 'is-invalid' : ''}`}>
			<div
				id="tc-datetime-picker-field-date"
				className="tc-datetime-picker-field">
				<Label
					className="sr-only"
					for={dateLabel}>
					<FormattedMessage {...messages.TC_COMPONENTS_DATETIME_PICKER_LABEL_TEXT_DATE} />
				</Label>
				<DatePicker
					name={dateLabel}
					value={formValues.date ? moment(formValues.date) : null}
					placeholder={intl.formatMessage(messages.TC_COMPONENTS_DATETIME_PICKER_LABEL_TEXT_DATE)}
					isInvalid={isInvalid}
					isRequired={isRequired}
					onChange={handleDateChange} />
			</div>
			<div
				id="tc-datetime-picker-field-time"
				className="tc-datetime-picker-field">
				<Label
					className="sr-only"
					for={timeLabel}>
					<FormattedMessage {...messages.TC_COMPONENTS_DATETIME_PICKER_LABEL_TEXT_TIME} />
				</Label>
				<TimePicker
					name={timeLabel}
					value={formValues.time || ''}
					placeholder={intl.formatMessage(messages.TC_COMPONENTS_DATETIME_PICKER_LABEL_TEXT_TIME)}
					isInvalid={isInvalid}
					isRequired={isRequired}
					onChange={handleTimeChange} />
			</div>
			<div id="tc-datetime-picker-field-timezone">
				<Label
					className="sr-only"
					for={timezoneLabel}>
					<FormattedMessage {...messages.TC_COMPONENTS_DATETIME_PICKER_LABEL_TEXT_TIMEZONE} />
				</Label>
				<TimezonePicker
					id={timezoneLabel}
					name={timezoneLabel}
					value={formValues.timezone}
					placeholder={intl.formatMessage(
						messages.TC_COMPONENTS_DATETIME_PICKER_LABEL_TEXT_TIMEZONE
					)}
					isInvalid={isInvalid}
					isRequired={isRequired}
					onChange={(newValue) => handleTimezoneChange(newValue)} />
			</div>
		</div>
	);

};

DateTimePicker.propTypes = propTypes;
DateTimePicker.defaultProps = defaultProps;

export default DateTimePicker;
